import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, mergeMap, pluck, switchMap } from 'rxjs/operators';

import { SuccessAlert } from '@shared/alert';
import { Candidate, DELETED_CANDIDATE } from '@shared/models';
import { SearchCandidatesService } from '@shared/services';
import { CandidateService } from '@shared/services/candidate.service';
import { isTruthy } from '@shared/utils';

import {
  candidatesGet,
  candidatesGetFail,
  candidatesGetSuccess,
  candidatesSelect,
  candidatesUpdate,
  candidatesUpdateFail,
  candidatesUpdateSuccess,
} from './candidates.actions';

@Injectable()
export class CandidatesEffects {
  select$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(candidatesSelect),
      pluck('id'),
      filter(isTruthy),
      map((id: number) => candidatesGet({ id })),
    ),
  );

  get$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(candidatesGet),
      pluck('id'),
      mergeMap((id) =>
        this.searchCandidatesService.get({ id }).pipe(
          map((response: Candidate) => candidatesGetSuccess({ payload: response })),
          catchError((error) => of(candidatesGetFail(error))),
        ),
      ),
    ),
  );

  update$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(candidatesUpdate),
      pluck('payload'),
      switchMap((payload: Partial<Candidate>) => {
        payload = {
          ...payload,
          socialInfo: !!payload.socialInfo ? payload.socialInfo.filter(({ url }) => !!url) : [],
        };
        return this.service.patch({ id: payload.id, body: payload }).pipe(
          map((response: Candidate) => {
            if (payload.status === DELETED_CANDIDATE) {
              this.store.dispatch(new SuccessAlert('The candidate has been successfully deleted'));
              this.router.navigateByUrl('/jobs');
            }
            return candidatesUpdateSuccess({ payload: response });
          }),
          catchError((error) => of(candidatesUpdateFail(error))),
        );
      }),
    ),
  );

  constructor(
    private router: Router,
    private actions$: Actions,
    private store: Store<{}>,
    private searchCandidatesService: SearchCandidatesService,
    private service: CandidateService,
  ) {}
}
